/**
 * Tenant-aware formatting glue — non-hook accessors for pure util modules and
 * call-time evaluation inside components that cannot (or should not) subscribe
 * to the tenant store.
 *
 * Resolution: hydrated tenant profile → persisted last-login profile →
 * platform-default literals (equal to today's hardcoded UAE behavior), with a
 * one-time console.error when serving defaults from an unhydrated store.
 */

import { useTenantStore } from '@/stores/tenant-store'

const warned = new Set<string>()

function warnOnce(key: string): void {
  if (warned.has(key)) return
  warned.add(key)
  console.error(
    `[tenant-format] profile not hydrated — falling back to platform default for ${key}`
  )
}

/** Currency + locale for money formatting. Falls back to AED / en-AE. */
export function getTenantMoney(): { currency: string; locale: string } {
  const profile = useTenantStore.getState().profile
  if (!profile) {
    warnOnce('money')
    return { currency: 'AED', locale: 'en-AE' }
  }
  return {
    currency: profile.baseCurrency || 'AED',
    locale: profile.locale || 'en-AE',
  }
}

/** Tax labels for display. Falls back to VAT / TRN. */
export function getTenantTax(): { taxLabel: string; taxIdLabel: string } {
  const profile = useTenantStore.getState().profile
  if (!profile) {
    warnOnce('tax')
    return { taxLabel: 'VAT', taxIdLabel: 'TRN' }
  }
  return {
    taxLabel: profile.taxLabel || 'VAT',
    taxIdLabel: profile.taxIdLabel || 'TRN',
  }
}

/** ISO-3166 alpha-2 tenant country code. Falls back to 'AE'. */
export function getTenantCountryCode(): string {
  const profile = useTenantStore.getState().profile
  if (!profile) {
    warnOnce('country')
    return 'AE'
  }
  return profile.country || 'AE'
}
